import "./characters.page.css";
import "./characters.create.css";

import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CharacterCard from "./CharacterCard";
import CreateCharacterDialog from "./CreateCharacterDialog";
import CenteredContainer from "../../components/layout/CenteredContainer/CenteredContainer";
import DeleteCharacterDialog from "./DeleteCharacterDialog";
import PageLoadState from "../../components/layout/PageLoadState/PageLoadState";
import { createCharacter, deleteCharacter } from "../../clients/characters.client";
import { fetchGet } from "../../clients/response";
import { SERVICE_URL } from "../../clients/app.const";
import { mapJsonToCharacter } from "../../models/Character";
import { systemLabelKey } from "../../catalogs/characterSystemCatalog";
import { selectCharacter } from "../../reducers/character.reducer";

const CharactersPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { tokenValue } = useSelector((state) => state.auth);
  const selectedCharacterId = useSelector(
    (state) => state.character.selectedCharacterId
  );
  const [characters, setCharacters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadState, setLoadState] = useState("loading");
  const [reloadKey, setReloadKey] = useState(0);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadCharacters = async () => {
      setLoadState("loading");
      const response = await fetchGet(
        `${SERVICE_URL}/characters`,
        { size: 100 },
        tokenValue
      );

      if (cancelled) return;
      if (response.isError) {
        setCharacters([]);
        setLoadState("error");
        return;
      }

      const summaries = response.data?.content ?? [];
      if (summaries.length === 0) {
        setCharacters([]);
        setLoadState("empty");
        return;
      }

      const loadedCharacters = await Promise.all(
        summaries.map(async (summary) => {
          if (summary.json) return mapJsonToCharacter(summary);

          const details = await fetchGet(
            `${SERVICE_URL}/characters/${summary.id}`,
            {},
            tokenValue
          );
          return mapJsonToCharacter(
            details.isError || !details.data ? summary : details.data
          );
        })
      );

      if (!cancelled) {
        setCharacters(loadedCharacters);
        setLoadState("ready");
      }
    };

    loadCharacters();
    return () => {
      cancelled = true;
    };
  }, [tokenValue, reloadKey]);

  const visibleCharacters = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return [...characters]
      .filter((character) => {
        if (!normalizedSearch) return true;
        const searchableValues = [
          character.characterName,
          character.race?.id && t(systemLabelKey("races", character.race.id)),
          character.background,
          character.alignmentId && t(systemLabelKey("alignments", character.alignmentId)),
          ...(character.characterClass ?? []).map(
            (characterClass) => characterClass.id && t(systemLabelKey("classes", characterClass.id))
          ),
        ];
        return searchableValues.some((value) =>
          String(value ?? "").toLowerCase().includes(normalizedSearch)
        );
      })
      .sort((first, second) =>
        first.characterName.localeCompare(second.characterName)
      );
  }, [characters, searchTerm, t]);

  const openCharacter = (characterId) => {
    dispatch(selectCharacter(characterId));
    navigate("/character");
  };

  const openCreateDialog = () => {
    setCreateError("");
    setIsCreateOpen(true);
  };

  const handleCreateCharacter = async (characterName) => {
    setIsCreating(true);
    setCreateError("");

    const response = await createCharacter(characterName, tokenValue);
    if (response.isError || !response.data?.id) {
      setCreateError(
        typeof response.data === "string" && response.data
          ? response.data
          : t("characters.createError")
      );
      setIsCreating(false);
      return;
    }

    const createdCharacter = mapJsonToCharacter(response.data);
    setCharacters((current) => [...current, createdCharacter]);
    setIsCreating(false);
    setIsCreateOpen(false);
    openCharacter(createdCharacter.id);
  };

  const showCatalog = loadState === "ready" || loadState === "empty";

  const openDeleteDialog = (character) => {
    setDeleteError("");
    setDeleteTarget(character);
  };

  const handleDeleteCharacter = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    setDeleteError("");

    const response = await deleteCharacter(deleteTarget.id, tokenValue);
    if (response.isError) {
      setDeleteError(
        typeof response.data === "string" && response.data
          ? response.data
          : t("characters.deleteError")
      );
      setIsDeleting(false);
      return;
    }

    const deletedId = deleteTarget.id;
    setCharacters((current) => current.filter((character) => character.id !== deletedId));
    if (selectedCharacterId === deletedId) {
      dispatch(selectCharacter(null));
    }
    setIsDeleting(false);
    setDeleteTarget(null);
  };

  return (
    <CenteredContainer>
      <PageLoadState
        state={loadState === "empty" ? "ready" : loadState}
        message={
          loadState === "loading"
            ? t("characters.loading")
            : t("characters.loadError")
        }
        onRetry={() => setReloadKey((key) => key + 1)}
      />

      {showCatalog && (
        <main className="characters-page">
          <header className="characters-page-header">
            <div>
              <p>{t("characters.eyebrow")}</p>
              <h1>{t("characters.title")}</h1>
              <span>{t("characters.subtitle")}</span>
            </div>
            <div className="characters-page-actions">
              {characters.length > 0 && (
                <label className="characters-search">
                  <span>{t("characters.searchLabel")}</span>
                  <input
                    type="search"
                    placeholder={t("characters.searchPlaceholder")}
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                </label>
              )}
              <button
                className="character-create-button"
                type="button"
                onClick={openCreateDialog}
              >
                + {t("characters.new")}
              </button>
            </div>
          </header>

          {characters.length === 0 ? (
            <div className="characters-empty-catalog">
              <div className="characters-empty-icon">+</div>
              <h2>{t("characters.emptyTitle")}</h2>
              <p>{t("characters.emptyText")}</p>
              <button type="button" onClick={openCreateDialog}>
                {t("characters.create")}
              </button>
            </div>
          ) : visibleCharacters.length > 0 ? (
            <div className="character-card-grid">
              {visibleCharacters.map((character) => (
                <CharacterCard
                  character={character}
                  isActive={character.id === selectedCharacterId}
                  key={character.id}
                  token={tokenValue}
                  onOpen={() => openCharacter(character.id)}
                  onDelete={openDeleteDialog}
                />
              ))}
            </div>
          ) : (
            <div className="characters-no-results">
              {t("characters.noMatch", { search: searchTerm })}
            </div>
          )}

          <CreateCharacterDialog
            error={createError}
            isCreating={isCreating}
            isOpen={isCreateOpen}
            onClose={() => !isCreating && setIsCreateOpen(false)}
            onCreate={handleCreateCharacter}
          />
        </main>
      )}

          <DeleteCharacterDialog
            character={deleteTarget}
            error={deleteError}
            isDeleting={isDeleting}
            onClose={() => !isDeleting && setDeleteTarget(null)}
            onConfirm={handleDeleteCharacter}
          />
    </CenteredContainer>
  );
};

export default CharactersPage;
